import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

import {
  EolRelease,
  FrameworkId,
} from '@internal/backstage-plugin-framework-versions-common';

import { EolApi } from './EolApi';

/**
 * Reads end-of-life data from the framework-versions backend, which caches
 * endoflife.date. Each framework is requested once per page load; a failed
 * request is retried on the next call.
 */
export class EndOfLifeDateClient implements EolApi {
  private readonly discoveryApi: Pick<DiscoveryApi, 'getBaseUrl'>;
  private readonly fetchApi: Pick<FetchApi, 'fetch'>;
  private readonly cache = new Map<FrameworkId, Promise<EolRelease[]>>();

  constructor(options: {
    discoveryApi: Pick<DiscoveryApi, 'getBaseUrl'>;
    fetchApi: Pick<FetchApi, 'fetch'>;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  getReleases(id: FrameworkId): Promise<EolRelease[]> {
    let request = this.cache.get(id);
    if (!request) {
      request = this.load(id).catch(error => {
        this.cache.delete(id);
        throw error;
      });
      this.cache.set(id, request);
    }
    return request;
  }

  private async load(id: FrameworkId): Promise<EolRelease[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('framework-versions');
    const response = await this.fetchApi.fetch(`${baseUrl}/eol/${id}`);
    if (!response.ok) throw new Error(response.statusText);
    const body: { releases: EolRelease[] } = await response.json();
    return body.releases;
  }
}
